// @flow

import * as React from 'react';

import get from 'lodash/get';

import type { DecoratedProduct } from '@gandi/cart';
import {
  ContactForm,
  CONTACTS_FORM,
  type Contact,
  type Fqdn,
  type FormValues,
  type Phase,
  type Process,
  type Role,
  type Tld,
} from '@gandi/domain-configuration';
import type { NotifyAction } from '@gandi/notifications';
import type { ResourceThunkAction } from '@gandi/resource/redux';
import type { TranslatorProps } from '@gandi/translate';
import { Button, Base, Spinner } from '@gandi/ui';

import { type FormComponentProps, CheckboxField } from 'gandi.react-form';

import type { GandiLogger } from 'gandi.redux-logger';

import { Intro } from '../../../../Layout';
import { Layout } from '../../../../components/Layout';
import type { Dispatch, FullState, WithRouterProps } from '../../../../types';
import InvitationCheckboxWrapper from '../../../Setup/RoleContact/components/InvitationCheckboxWrapper';

export type InjectedProps = {
  // withXXX
  translator: TranslatorProps,
  logger: GandiLogger,
  form: FormComponentProps,
  ...WithRouterProps<{
    cartId: string,
    productId: string,
    role: Role,
  }>,
};

export type DispatchProps = {
  fetchTldInfo: string => ResourceThunkAction<Dispatch, FullState>,
  notifyDanger: NotifyAction,
};

export type StateProps = {
  fqdn: Fqdn,
  phase: Phase,
  process: Process,
  contact: { ...Partial<Contact>, ... },
  backUrl: string,
  cartIsWorking: boolean,
  isSubmitting: boolean,
  /* eslint-disable react/no-unused-prop-types */
  product: void | DecoratedProduct,
  contactValues: FormValues,
  /* eslint-enable react/no-unused-prop-types */
};

export type Props = {
  handleSubmit: (event: SyntheticEvent<>) => mixed,
  ...InjectedProps,
  ...DispatchProps,
  ...StateProps,
};

type State = {
  isWorking: boolean,
  tld?: Tld,
};

export default class DomainContactEditionPresenter extends React.Component<
  Props,
  State,
> {
  state: State = {
    isWorking: true,
    tld: undefined,
  };

  translations: { admin: any, bill: any, owner: any, tech: any } = {
    owner: this.props.translator.__('Edit the owner contact'),
    admin: this.props.translator.__('Edit the admin contact'),
    tech: this.props.translator.__('Edit the tech contact'),
    bill: this.props.translator.__('Edit the bill contact'),
  };

  async componentDidMount() {
    const {
      translator: { __ },
      fqdn,
      logger,
    } = this.props;

    try {
      const response = await this.props.fetchTldInfo(fqdn);
      this.setState({
        tld: get(response, 'data.name'),
        isWorking: false,
      });
    } catch (err) {
      logger.error({ err }, 'Error when fetching tld infos.');
      this.props.notifyDanger(__('Unexpected error occurred'));
      this.setState({
        isWorking: false,
      });
    }
  }

  renderLoader(): null | React.Node {
    if (this.state.isWorking) {
      return <Spinner centered />;
    }

    if (this.props.cartIsWorking) {
      return <Spinner centered />;
    }

    return null;
  }

  renderForm(): null | React.Node {
    const formButtons = [
      <Button
        to={this.props.isSubmitting ? undefined : this.props.backUrl}
        key="cancel"
        disabled={this.props.isSubmitting}
        kind="outline"
        grouped
      >
        {this.props.translator.__('Cancel')}
      </Button>,
      <Button
        form={CONTACTS_FORM}
        loading={this.props.isSubmitting}
        key="submit"
        grouped
      >
        {this.props.translator.__('Submit')}
      </Button>,
    ];

    const {
      params: { role },
      fqdn,
      phase,
      process,
      contact,
      cartIsWorking,
      form,
    } = this.props;

    if (this.state.isWorking || !this.state.tld) {
      return null;
    }

    return (
      <>
        <Base marginBottom="base" className="wrapper-plain">
          <ContactForm
            fqdn={fqdn}
            tld={this.state.tld}
            role={role}
            phase={phase}
            process={process}
            initialValues={contact}
            handleSubmit={form.handleSubmit}
            isWorking={cartIsWorking}
            formProps={form}
          />
        </Base>
        <Base marginBottom="base">
          <InvitationCheckboxWrapper>
            <CheckboxField name="send_invitation">
              {this.props.translator.__(
                'Email an invitation to the contact, allowing them to manage the domain.',
              )}
            </CheckboxField>
          </InvitationCheckboxWrapper>
        </Base>
        <Base marginBottom="base" flex flexJustifyBetween flexWrap>
          {formButtons}
        </Base>
      </>
    );
  }

  render(): React.Node {
    return (
      <Layout>
        <Intro
          title={
            this.translations[this.props.params.role] ||
            this.props.translator.__('Edit the contact')
          }
        />
        {this.renderLoader()}
        {this.renderForm()}
      </Layout>
    );
  }
}
